/**
 * The internal access lock in words (plan-029 WAPP-ACCESS; design §40.7.4, §40.25.2; D-12 as changed at AP-0,
 * DD29-21): which state an agent is in, and the warning the agent page draws above every tab but Channels (where the
 * access panel says it itself).
 *
 * ```
 *   lock_pending   "This agent will soon answer only the people on its list. Add them now, or it will refuse every caller."
 *   locked         "This agent refuses every caller until you add the people who may call it."
 * ```
 *
 * WHERE THE STATE COMES FROM (CHANGED 2026-10-28, plan-029 ACCESS-SPLIT). The access read's `lock_state`, which the api
 * computes from its twin of the internal access lock: `pending` before the lock, `locked` once it is on — or already
 * while the list is on but empty, which admits nobody whatever the switches say. Every reader of the agent sees it; the
 * notice feed, which addresses only the people who may add the people, is no longer read for it.
 */
import { Icon } from "@wetalk/ui";
import type { ReactNode } from "react";

import "../../new/answering.css";

import { useVoiceAgentAccess, type PartyAccessView } from "../../../../api/slice/party-access.js";
import { portalText, usePortalTranslation } from "../../../../i18n/portal.js";
import { GLYPH } from "../../../../shell/glyph.js";

/** The internal access lock as the portal draws it (`data-access-lock`): it will soon refuse every caller, or it does. */
export type InternalAccessLockState = "lock_pending" | "locked";

/**
 * The lock an agent is under, from its access read; `null` when it answers as its list says (or the read is pending).
 * A list switched on with no one on it refuses every caller whatever the agent's audience, so it reads `locked` too.
 */
export function partyAccessLockOf(access: PartyAccessView | undefined): InternalAccessLockState | null {
  if (access === undefined || !access.available) return null;
  if (access.enabled && access.entryCount === 0 && !access.anySignedVisitor) return "locked";
  if (access.lockState === "locked") return "locked";
  if (access.lockState === "pending") return "lock_pending";
  return null;
}

/** The sentence for a lock state. */
export function partyAccessLockTextOf(lock: InternalAccessLockState): string {
  return lock === "locked" ? portalText("callerProtection", "access.locked") : portalText("callerProtection", "access.lock_pending");
}

export interface PartyAccessLockWarningProps {
  readonly voiceAgentId: string;
  /** "Choose who may call": the Channels tab, where the list is edited. */
  readonly onOpenAccess: () => void;
}

/** The agent page's warning above the tabs, for an agent that is (or soon will be) locked. Nothing otherwise. */
export function PartyAccessLockWarning({ voiceAgentId, onOpenAccess }: PartyAccessLockWarningProps): ReactNode {
  usePortalTranslation("callerProtection");
  const access = useVoiceAgentAccess(voiceAgentId);
  const lock = partyAccessLockOf(access.data);
  if (lock === null) return null;
  return (
    <p className="c-answer-warn" role="status" data-access-lock={lock}>
      <Icon glyph={GLYPH.warning} size="lg" />
      <span>
        <strong>{partyAccessLockTextOf(lock)}</strong>{" "}
        <a
          href="#"
          onClick={(event) => {
            event.preventDefault();
            onOpenAccess();
          }}
        >
          {portalText("callerProtection", "access.open_channels")}
        </a>
      </span>
    </p>
  );
}
